/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { forgotPassword } from '../models/forgotPassword';
import type { Login } from '../models/Login';
import type { meta } from '../models/meta';
import type { mfaTypeEnum } from '../models/mfaTypeEnum';
import type { OAuthAppsItem } from '../models/OAuthAppsItem';
import type { PermissionGridItem } from '../models/PermissionGridItem';
import type { RoleItem } from '../models/RoleItem';
import type { TenantRecordItem } from '../models/TenantRecordItem';
import type { Token } from '../models/Token';
import type { userChangePassword } from '../models/userChangePassword';
import type { UserPartialItem } from '../models/UserPartialItem';
import type { UserPermissionsMatrix } from '../models/UserPermissionsMatrix';
import { request as __request } from '../core/request';

export class AuthService {

    /**
     * Revert to authoirzed user from login-as
     * Cancel Login-as
     * @result any Successful operation
     * @throws ApiError
     */
    public static async cancelLoginAs(): Promise<{
        /**
         * Session token
         */
        token: string,
        /**
         * User MFA Available
         */
        mfaAvail?: Array<mfaTypeEnum>,
        /**
         * User MFA Used
         */
        mfaUsed?: string,
        /**
         * User MFA Registered Phone #
         */
        mfaPhone?: string,
        /**
         * User MFA Validation Status
         */
        mfaValidated?: boolean,
        /**
         * Token Expiration
         */
        exp: number,
        /**
         * Users theme
         */
        theme?: string,
        /**
         * User can post new queries
         */
        canPost?: boolean,
        user: UserPartialItem,
        userPermissionsMatrix?: UserPermissionsMatrix,
    }> {
        const result = await __request({
            method: 'POST',
            path: `/auth/cancelloginas`,
            errors: {
                400: `Authentication error response`,
                422: `Error: Unprocessable Entity`,
            },
        });
        return result.body;
    }

    /**
     * Request a password reset
     * Generate a password reset based on email
     * @result any successful operation
     * @throws ApiError
     */
    public static async forgotPassword({
        requestBody,
    }: {
        /** Forgot Password Request **/
        requestBody: forgotPassword,
    }): Promise<any> {
        const result = await __request({
            method: 'POST',
            path: `/auth/forgotPassword`,
            body: requestBody,
            errors: {
                400: `Error: Update Failed`,
                403: `Error: Forbidden`,
                404: `Error: Not Found`,
                422: `Error: Unprocessable Entity`,
            },
        });
        return result.body;
    }

    /**
     * Find Permissions  by Role ID.
     * Returns all permissions for a given Role.  No Pagination
     * @result PermissionGridItem Permission Grid Item
     * @throws ApiError
     */
    public static async getPermissionsByRoleId({
        id,
    }: {
        /** Record ID **/
        id: number,
    }): Promise<PermissionGridItem> {
        const result = await __request({
            method: 'GET',
            path: `/auth/role/${id}/permissions`,
            errors: {
                403: `Error: Forbidden`,
                404: `Error: Not Found`,
                422: `Error: Unprocessable Entity`,
            },
        });
        return result.body;
    }

    /**
     * Find role  by ID.
     * Returns matching record
     * @result RoleItem successful operation
     * @throws ApiError
     */
    public static async getRoleById({
        id,
    }: {
        /** Record ID **/
        id: number,
    }): Promise<RoleItem> {
        const result = await __request({
            method: 'GET',
            path: `/auth/role/${id}`,
            errors: {
                403: `Error: Forbidden`,
                404: `Error: Not Found`,
                422: `Error: Unprocessable Entity`,
            },
        });
        return result.body;
    }

    /**
     * Get Roles List
     * Returns one or more matching records
     * @result any successful operation
     * @throws ApiError
     */
    public static async getRolesList({
        page = 1,
        pageSize = 10,
        sort,
    }: {
        /** Page to begin results at **/
        page?: number,
        /** Number of results per page **/
        pageSize?: number,
        /** Sort Options - Comma delimited list of key:order. order optional - default ASC  **/
        sort?: string,
    }): Promise<{
        data?: Array<RoleItem>,
        meta?: meta,
    }> {
        const result = await __request({
            method: 'GET',
            path: `/auth/role/list`,
            query: {
                'page': page,
                'pageSize': pageSize,
                'sort': sort,
            },
            errors: {
                403: `Error: Forbidden`,
                422: `Error: Unprocessable Entity`,
            },
        });
        return result.body;
    }

    /**
     * Login As another User
     * Login as a specified user
     * @result any successful operation
     * @throws ApiError
     */
    public static async loginAs({
        userId,
    }: {
        /** The User Id of the target user **/
        userId: string,
    }): Promise<{
        /**
         * Session token
         */
        token: string,
        /**
         * User MFA Available
         */
        mfaAvail?: Array<mfaTypeEnum>,
        /**
         * User MFA Used
         */
        mfaUsed?: string,
        /**
         * User MFA Registered Phone #
         */
        mfaPhone?: string,
        /**
         * User MFA Validation Status
         */
        mfaValidated?: boolean,
        /**
         * Token Expiration
         */
        exp: number,
        /**
         * Users theme
         */
        theme?: string,
        /**
         * User can post new queries
         */
        canPost?: boolean,
        /**
         * default Plan DraftId
         */
        defaultPlanDraftId?: number,
        user: UserPartialItem,
        authUser: UserPartialItem,
        userPermissionsMatrix?: UserPermissionsMatrix,
    }> {
        const result = await __request({
            method: 'POST',
            path: `/auth/loginas/${userId}`,
            errors: {
                400: `Authentication error response`,
                422: `Error: Unprocessable Entity`,
            },
        });
        return result.body;
    }

    /**
     * User Logout
     * Logs out current logged in user session
     * @result any Successful Operation - No Content
     * @throws ApiError
     */
    public static async logout(): Promise<any> {
        const result = await __request({
            method: 'POST',
            path: `/auth/logout`,
            errors: {
                403: `Error: Forbidden`,
            },
        });
        return result.body;
    }

    /**
     * MFA Auth app Init
     * MFA Auth app Init
     * @result any QR code image data URI for MFA authenticator app setup
     * @throws ApiError
     */
    public static async getMfaAuthAppInit(): Promise<{
        data?: {
            /**
             * Base64-encoded image data URI representing the QR code to be scanned by the user's authenticator app
             */
            qrCodeDataUri?: string,
        },
    }> {
        const result = await __request({
            method: 'GET',
            path: `/auth/mfa/authapp/init`,
            errors: {
                403: `Error: Forbidden`,
                422: `Error: Unprocessable Entity`,
            },
        });
        return result.body;
    }

    /**
     * MFA Request Code
     * MFA Request for Alternative Code
     * @result any Result Success / Fail
     * @throws ApiError
     */
    public static async getMfaRequestCode({
        mfaType,
    }: {
        /** MFA Type **/
        mfaType?: mfaTypeEnum,
    }): Promise<{
        /**
         * Response Code
         */
        result?: string,
    }> {
        const result = await __request({
            method: 'GET',
            path: `/auth/mfa/requestCode`,
            query: {
                'mfaType': mfaType,
            },
            errors: {
                403: `Error: Forbidden`,
                422: `Error: Unprocessable Entity`,
            },
        });
        return result.body;
    }

    /**
     * MFA SMS Init
     * MFA SMS Init
     * @result any Successful Operation - No Content
     * @throws ApiError
     */
    public static async getMfasmsInit({
        phone,
    }: {
        /** Telephone number **/
        phone?: string,
    }): Promise<any> {
        const result = await __request({
            method: 'GET',
            path: `/auth/mfa/sms/init`,
            query: {
                'phone': phone,
            },
            errors: {
                403: `Error: Forbidden`,
                422: `Error: Unprocessable Entity`,
            },
        });
        return result.body;
    }

    /**
     * MFA verify - Must Match Auth Token
     * MFA verify
     * @result any Result Success / Fail
     * @throws ApiError
     */
    public static async getMfaVerify({
        authCode,
        mfaType,
    }: {
        /** Authorization Code - Allow for leading 0s **/
        authCode?: string,
        /** MFA Type **/
        mfaType?: mfaTypeEnum,
    }): Promise<{
        /**
         * Response Code
         */
        result?: string,
    }> {
        const result = await __request({
            method: 'GET',
            path: `/auth/mfa/verify`,
            query: {
                'authCode': authCode,
                'mfaType': mfaType,
            },
            errors: {
                403: `Error: Forbidden`,
                422: `Error: Unprocessable Entity`,
            },
        });
        return result.body;
    }

    /**
     * List OAuth App connectors
     * Data for MS365 OUath integration
     * @result any successful operation
     * @throws ApiError
     */
    public static async getOauthApps(): Promise<{
        data: Array<OAuthAppsItem>,
    }> {
        const result = await __request({
            method: 'GET',
            path: `/auth/oauthApps`,
        });
        return result.body;
    }

    /**
     * Register User Device - Requires Valid WebLogin Session
     * Stores device registration for long lived sessions.
     * @result any successful operation
     * @throws ApiError
     */
    public static async registerDevice({
        requestBody,
    }: {
        /** Device Registration **/
        requestBody: Token,
    }): Promise<{
        /**
         * Device token
         */
        deviceToken?: string,
        /**
         * Token Expiration
         */
        exp?: number,
    }> {
        const result = await __request({
            method: 'POST',
            path: `/auth/registerDevice`,
            body: requestBody,
            errors: {
                400: `Authentication error response`,
                401: `Authentication error response`,
                422: `Error: Unprocessable Entity`,
            },
        });
        return result.body;
    }

    /**
     * User Tenant Selection
     * User Tenant Selection
     * @result TenantRecordItem successful operation
     * @throws ApiError
     */
    public static async setTenantSelection({
        tenantIdPath,
    }: {
        /** tenant ID **/
        tenantIdPath: number,
    }): Promise<TenantRecordItem> {
        const result = await __request({
            method: 'POST',
            path: `/auth/tenant/select/${tenantIdPath}`,
            errors: {
                403: `Error: Forbidden`,
                422: `Error: Unprocessable Entity`,
            },
        });
        return result.body;
    }

    /**
     * Validate Users Reset Password Token
     * This should be called before presenting user with a reset password page
     * @result any Successful operation
     * @throws ApiError
     */
    public static async validateUserPasswordToken({
        id,
        token,
    }: {
        /** Record ID **/
        id: number,
        /** Authentication Token **/
        token: string,
    }): Promise<any> {
        const result = await __request({
            method: 'GET',
            path: `/auth/resetpass/${id}/${token}`,
            errors: {
                400: `Error: Update Failed`,
                403: `Error: Forbidden`,
                404: `Error: Not Found`,
                422: `Error: Unprocessable Entity`,
            },
        });
        return result.body;
    }

    /**
     * Update an existing users password
     * Update a record based on data given
     * @result any successful operation
     * @throws ApiError
     */
    public static async updateUserPasswordToken({
        id,
        token,
        requestBody,
    }: {
        /** Record ID **/
        id: number,
        /** Authentication Token **/
        token: string,
        /** user object that needs to be updated **/
        requestBody: userChangePassword,
    }): Promise<{
        /**
         * Username
         */
        username?: string,
    }> {
        const result = await __request({
            method: 'PUT',
            path: `/auth/resetpass/${id}/${token}`,
            body: requestBody,
            errors: {
                400: `Error: Update Failed`,
                403: `Error: Forbidden`,
                404: `Error: Not Found`,
                422: `Error: Unprocessable Entity`,
            },
        });
        return result.body;
    }

    /**
     * User Login
     * Logs user into the system
     * @result any successful operation
     * @throws ApiError
     */
    public static async webLogin({
        requestBody,
        authorization,
    }: {
        /** Web Login **/
        requestBody: Login,
        /**  **/
        authorization?: string,
    }): Promise<{
        /**
         * Session token
         */
        token?: string,
        /**
         * Token Expiration
         */
        exp?: number,
        /**
         * User MFA Available
         */
        mfaAvail?: Array<mfaTypeEnum>,
        /**
         * User MFA Used
         */
        mfaUsed?: string,
        /**
         * User MFA Registered Phone #
         */
        mfaPhone?: string,
        /**
         * User MFA Validation Status
         */
        mfaValidated?: boolean,
        /**
         * Password reset token
         */
        resetToken?: string,
        /**
         * User must reset password
         */
        requirePassReset?: boolean,
        user?: UserPartialItem,
        /**
         * User can post new queries
         */
        canPost?: boolean,
        /**
         * default Plan DraftId
         */
        defaultPlanDraftId?: number,
        /**
         * Users theme
         */
        theme?: string,
        userPermissionsMatrix?: UserPermissionsMatrix,
    }> {
        const result = await __request({
            method: 'POST',
            path: `/auth/webLogin`,
            headers: {
                'Authorization': authorization,
            },
            body: requestBody,
            errors: {
                400: `Authentication error response`,
                401: `Authentication error response`,
                422: `Error: Unprocessable Entity`,
            },
        });
        return result.body;
    }

}